import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2, Search, Trash2, Pencil, UploadCloud, LayoutList, Image as ImageIcon, Check, X, Sparkles,
} from 'lucide-react';
import { API_BASE } from '../config/api';

const SECTION_ROLES = ['hero', 'content', 'gallery', 'cta', 'quiz', 'footer', 'other'];
const IMAGE_ROLES = ['hero', 'hero_bg', 'hero_side', 'content', 'gallery', 'background', 'icon', 'people', 'illustration', 'logo', 'other'];
const ORIENTATIONS = ['landscape', 'portrait', 'square', 'ultrawide'];
const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];

function parseTags(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  try {
    const j = JSON.parse(raw);
    if (Array.isArray(j)) return j.map((s) => String(s).trim()).filter(Boolean);
  } catch { /* */ }
  return String(raw).split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
}

/**
 * Painel lateral exclusivo da conta Mestra (curadoria IA).
 * Abas: Seções Globais | Imagens Globais.
 */
export default function AiLibraryPanel({ authToken, onInsertSection, onOpenGenerateLayout }) {
  const { t } = useTranslation('builder');
  const [tab, setTab] = useState('sections'); /* sections | images */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [sections, setSections] = useState([]);
  const [assets, setAssets] = useState([]);

  const [editingSection, setEditingSection] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadRole, setUploadRole] = useState('hero_bg');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadOrient, setUploadOrient] = useState(''); /* auto se vazio */

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }),
    [authToken]
  );

  const loadSections = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE}/section-presets/ai-global`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setSections(Array.isArray(d.sections) ? d.sections : []);
    } catch (e) {
      setSections([]);
      setError(e.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const loadAssets = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '120', offset: '0' });
      if (q.trim()) params.set('q', q.trim());
      if (roleFilter) params.set('image_role', roleFilter);
      const r = await fetch(`${API_BASE}/ai-media?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setAssets(Array.isArray(d.assets) ? d.assets : []);
    } catch (e) {
      setAssets([]);
      setError(e.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, [authToken, q, roleFilter]);

  useEffect(() => {
    if (tab === 'sections') loadSections();
    else loadAssets();
  }, [tab, loadSections, loadAssets]);

  const filteredSections = useMemo(() => {
    let list = sections;
    if (roleFilter) list = list.filter((s) => String(s.section_role || '') === roleFilter);
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((s) => {
      const blob = [
        s.name, s.description, s.section_role, s.status, s.mood,
        parseTags(s.tags).join(' '),
      ].join(' ').toLowerCase();
      return blob.includes(needle);
    });
  }, [sections, roleFilter, q]);

  const deleteSection = async (id) => {
    if (!window.confirm(t('aiLibDeleteSection', { defaultValue: 'Excluir esta seção Global?' }))) return;
    const r = await fetch(`${API_BASE}/section-presets/ai-global/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      alert(d.error || 'Erro');
      return;
    }
    if (editingSection?.id === id) setEditingSection(null);
    loadSections();
  };

  const saveSectionMeta = async () => {
    if (!editingSection?.id) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/section-presets/ai-global/${editingSection.id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          name: editingSection.name,
          description: editingSection.description,
          section_role: editingSection.section_role,
          tags: editingSection.tagsText,
          tier_min: editingSection.tier_min,
          mood: editingSection.mood,
          status: editingSection.status,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      setEditingSection(null);
      await loadSections();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm(t('aiLibDeleteImage', { defaultValue: 'Excluir esta imagem da biblioteca IA?' }))) return;
    const r = await fetch(`${API_BASE}/ai-media/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      alert(d.error || 'Erro');
      return;
    }
    if (editingAsset?.id === id) setEditingAsset(null);
    loadAssets();
  };

  const saveAssetMeta = async () => {
    if (!editingAsset?.id) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/ai-media/${editingAsset.id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          display_name: editingAsset.display_name,
          tags: editingAsset.tagsText,
          image_role: editingAsset.image_role,
          mood: editingAsset.mood,
          status: editingAsset.status,
          orientation: editingAsset.orientation,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      setEditingAsset(null);
      await loadAssets();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (files) => {
    if (!files?.length || !authToken) return;
    const valid = Array.from(files).filter((f) => ACCEPTED.includes(f.type));
    if (!valid.length) {
      alert(t('uploadErrorTypes', { defaultValue: 'Use apenas: JPG, PNG, WebP, GIF ou SVG.' }));
      return;
    }
    setUploading(true);
    const fd = new FormData();
    valid.forEach((f) => fd.append('files', f));
    fd.append('tags', uploadTags);
    fd.append('image_role', uploadRole);
    fd.append('status', 'ready');
    if (uploadOrient) fd.append('orientation', uploadOrient);
    try {
      const r = await fetch(`${API_BASE}/ai-media/batch-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      await loadAssets();
    } catch (e) {
      alert(e.message);
    } finally {
      setUploading(false);
    }
  };

  const roleChips = tab === 'sections' ? SECTION_ROLES : IMAGE_ROLES;

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
        {t('aiLibHint', {
          defaultValue:
            'Curadoria Mestra: organize seções (hero, conteúdo…) e imagens com papel + formato. Na montagem, a IA escolhe por esses metadados — não inventa o uso da foto.',
        })}
      </p>

      {typeof onOpenGenerateLayout === 'function' && (
        <button
          type="button"
          onClick={onOpenGenerateLayout}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 shadow-sm"
        >
          <Sparkles size={16} />
          {t('aiLayoutOpenBtn', { defaultValue: 'Gerar layout (teste Mestra)' })}
        </button>
      )}

      <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden text-xs font-semibold">
        <button
          type="button"
          onClick={() => { setTab('sections'); setRoleFilter(''); setQ(''); }}
          className={`flex-1 px-2 py-2 inline-flex items-center justify-center gap-1 ${tab === 'sections' ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
        >
          <LayoutList size={14} />
          {t('aiLibTabSections', { defaultValue: 'Seções' })}
        </button>
        <button
          type="button"
          onClick={() => { setTab('images'); setRoleFilter(''); setQ(''); }}
          className={`flex-1 px-2 py-2 inline-flex items-center justify-center gap-1 ${tab === 'images' ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
        >
          <ImageIcon size={14} />
          {t('aiLibTabImages', { defaultValue: 'Imagens' })}
        </button>
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('aiLibSearch', { defaultValue: 'Buscar…' })}
          className="w-full pl-8 pr-2 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900"
        />
      </div>

      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => setRoleFilter('')}
          className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${!roleFilter ? 'bg-violet-100 border-violet-300 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-700' : 'border-slate-200 dark:border-slate-600 text-slate-500'}`}
        >
          {t('aiLibAll', { defaultValue: 'Todos' })}
        </button>
        {roleChips.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRoleFilter(r)}
            className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${roleFilter === r ? 'bg-violet-100 border-violet-300 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-700' : 'border-slate-200 dark:border-slate-600 text-slate-500'}`}
          >
            {r}
          </button>
        ))}
      </div>

      {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}

      {tab === 'images' && (
        <div className="rounded-xl border border-dashed border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/20 p-3 space-y-2">
          <p className="text-[10px] font-semibold text-violet-800 dark:text-violet-200">
            {t('aiLibUploadTitle', { defaultValue: 'Enviar imagem para a IA' })}
          </p>
          <select
            value={uploadRole}
            onChange={(e) => setUploadRole(e.target.value)}
            className="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-1.5 bg-white dark:bg-slate-900"
          >
            {IMAGE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select
            value={uploadOrient}
            onChange={(e) => setUploadOrient(e.target.value)}
            className="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-1.5 bg-white dark:bg-slate-900"
          >
            <option value="">{t('aiLibOrientAuto', { defaultValue: 'Formato: detectar automático' })}</option>
            {ORIENTATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <input
            value={uploadTags}
            onChange={(e) => setUploadTags(e.target.value)}
            placeholder={t('aiLibTagsPh', { defaultValue: 'Tags: excel, negócios, pessoas…' })}
            className="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-600 px-2 py-1.5 bg-white dark:bg-slate-900"
          />
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-violet-700 dark:text-violet-300 cursor-pointer">
            <UploadCloud size={16} />
            {uploading ? <Loader2 size={14} className="animate-spin" /> : t('mediaUploadDrop', { defaultValue: 'Enviar imagens' })}
            <input
              type="file"
              accept={ACCEPTED.join(',')}
              multiple
              className="hidden"
              disabled={uploading}
              onChange={(e) => { onUpload(e.target.files); e.target.value = ''; }}
            />
          </label>
          <p className="text-[10px] text-slate-500 dark:text-slate-400">
            {t('aiLibOrientHelp', {
              defaultValue: 'hero_bg = fundo largo; hero_side = foto lateral; orientation diz se é paisagem, retrato ou quadrada.',
            })}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-400 gap-2 text-sm">
          <Loader2 className="animate-spin" size={18} />
          {t('loading', { defaultValue: 'Carregando…' })}
        </div>
      ) : tab === 'sections' ? (
        <ul className="space-y-2">
          {filteredSections.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">
              {t('sectionPresetAiEmpty', { defaultValue: 'Nenhuma seção Global ainda.' })}
            </p>
          ) : filteredSections.map((s) => (
            <li key={s.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-2.5 bg-slate-50/80 dark:bg-slate-900/40">
              {editingSection?.id === s.id ? (
                <div className="space-y-2">
                  <input
                    value={editingSection.name}
                    onChange={(e) => setEditingSection((p) => ({ ...p, name: e.target.value }))}
                    className="w-full text-xs rounded border border-slate-200 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-900"
                  />
                  <select
                    value={editingSection.section_role || 'content'}
                    onChange={(e) => setEditingSection((p) => ({ ...p, section_role: e.target.value }))}
                    className="w-full text-xs rounded border border-slate-200 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-900"
                  >
                    {SECTION_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select
                    value={editingSection.status || 'ready'}
                    onChange={(e) => setEditingSection((p) => ({ ...p, status: e.target.value }))}
                    className="w-full text-xs rounded border border-slate-200 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-900"
                  >
                    <option value="draft">draft</option>
                    <option value="ready">ready</option>
                    <option value="archived">archived</option>
                  </select>
                  <input
                    value={editingSection.tagsText || ''}
                    onChange={(e) => setEditingSection((p) => ({ ...p, tagsText: e.target.value }))}
                    placeholder="tags"
                    className="w-full text-xs rounded border border-slate-200 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-900"
                  />
                  <div className="flex gap-1">
                    <button type="button" onClick={saveSectionMeta} disabled={saving} className="p-1.5 rounded-lg bg-violet-600 text-white"><Check size={14} /></button>
                    <button type="button" onClick={() => setEditingSection(null)} className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600"><X size={14} /></button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{s.name}</div>
                    <div className="text-[10px] text-violet-600 dark:text-violet-300 mt-0.5">
                      {[s.section_role, s.status, s.tier_min].filter(Boolean).join(' · ')}
                    </div>
                    {parseTags(s.tags).length > 0 && (
                      <div className="text-[10px] text-slate-400 mt-0.5 truncate">{parseTags(s.tags).join(', ')}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5 shrink-0">
                    {typeof onInsertSection === 'function' && (
                      <button
                        type="button"
                        title={t('aiLibInsertValidate', { defaultValue: 'Inserir no palco' })}
                        onClick={() => onInsertSection(s)}
                        className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30"
                      >
                        <LayoutList size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditingSection({
                        ...s,
                        tagsText: parseTags(s.tags).join(', '),
                      })}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSection(s.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="space-y-2">
          {assets.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">
              {t('aiLibImagesEmpty', { defaultValue: 'Nenhuma imagem Global ainda.' })}
            </p>
          ) : assets.map((a) => (
            <li key={a.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-2 bg-slate-50/80 dark:bg-slate-900/40">
              {editingAsset?.id === a.id ? (
                <div className="space-y-2">
                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700">
                    <img src={a.url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <input
                    value={editingAsset.display_name || ''}
                    onChange={(e) => setEditingAsset((p) => ({ ...p, display_name: e.target.value }))}
                    className="w-full text-xs rounded border border-slate-200 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-900"
                  />
                  <select
                    value={editingAsset.image_role || 'content'}
                    onChange={(e) => setEditingAsset((p) => ({ ...p, image_role: e.target.value }))}
                    className="w-full text-xs rounded border border-slate-200 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-900"
                  >
                    {IMAGE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select
                    value={editingAsset.orientation || ''}
                    onChange={(e) => setEditingAsset((p) => ({ ...p, orientation: e.target.value || null }))}
                    className="w-full text-xs rounded border border-slate-200 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-900"
                  >
                    <option value="">—</option>
                    {ORIENTATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input
                    value={editingAsset.tagsText || ''}
                    onChange={(e) => setEditingAsset((p) => ({ ...p, tagsText: e.target.value }))}
                    className="w-full text-xs rounded border border-slate-200 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-900"
                    placeholder="tags"
                  />
                  <div className="flex gap-1">
                    <button type="button" onClick={saveAssetMeta} disabled={saving} className="p-1.5 rounded-lg bg-violet-600 text-white"><Check size={14} /></button>
                    <button type="button" onClick={() => setEditingAsset(null)} className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600"><X size={14} /></button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700 shrink-0">
                    <img src={a.url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{a.display_name || a.filename}</div>
                    <div className="text-[10px] text-violet-600 dark:text-violet-300">
                      {[a.image_role, a.orientation].filter(Boolean).join(' · ')}
                      {a.width_px && a.height_px ? ` · ${a.width_px}×${a.height_px}` : ''}
                    </div>
                    {parseTags(a.tags).length > 0 && (
                      <div className="text-[10px] text-slate-400 truncate">{parseTags(a.tags).join(', ')}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditingAsset({
                        ...a,
                        tagsText: parseTags(a.tags).join(', '),
                      })}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteAsset(a.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
